import React from "react";
import { FieldMetadata, getInputProps, useInputControl } from "@conform-to/react";
import { FormControl, FormHelperText, TextField, TextFieldProps } from "@mui/material";
import { ErrorParagraph } from "components/ErrorParagraph";

export type InputProps = TextFieldProps & {
  errors?: string[];
  width?: string;
  tableHeadStyle?: boolean;
};

export function Input({ errors, tableHeadStyle, ...props }: InputProps) {
  const width = props.fullWidth ? "100%" : (props.width ?? "300px");
  return (
    <FormControl sx={{ width }}>
      <TextField
        variant="filled"
        size="small"
        margin="none"
        {...props}
        sx={{
          width,

          ...(tableHeadStyle && {
            "& .MuiFilledInput-root": {
              backgroundColor: "#00103aff !important",
              borderRadius: "20px !important",
              paddingTop: 0,
            },

            "& .MuiFilledInput-root:hover": {
              backgroundColor: "primary.dark",
            },

            "& .MuiFilledInput-input": {
              fontSize: 12,
              color: "white",
            },

            "& .MuiInputLabel-root": {
              color: "white !important",
            },

            "& .MuiInputLabel-root.Mui-focused": {
              color: "white",
            },
            "& .MuiFilledInput-root::before": {
              borderBottom: "none !important",
            },
          }),
        }}
        helperText={null}
        SelectProps={{
          displayEmpty: true,
          ...props.SelectProps,
        }}
      />
      {errors && errors.length > 0 && (
        <FormHelperText error>
          {errors.map((text) => (
            <ErrorParagraph error={text} key={text} inputFieldError />
          ))}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export type InputConformProps = TextFieldProps & {
  field: FieldMetadata<any, any, any>;
  showErrors?: boolean;
  width?: string;
  tableHeadStyle?: boolean;
  onMaskAccept?: (value: string) => void;
  preProcessValue?: (value: string) => string;
};

export function InputConform({
  label,
  field,
  // on extrait explicitement les handlers pour éviter qu'ils soient écrasés
  preProcessValue,
  onChange: propOnChange,
  onFocus: propOnFocus,
  onBlur: propOnBlur,
  onMaskAccept: propOnMaskAccept,
  showErrors = true,
  ...restProps
}: InputConformProps) {
  const control = useInputControl(field as any);

  const errors =
    showErrors && Array.isArray(field.errors) && field.errors.length > 0
      ? field.errors.map((e) => (typeof e === "string" ? e : (e?.message ?? "")))
      : [];

  const value = Array.isArray(control.value) ? (control.value[0] ?? "") : (control.value ?? "");

  // handlers locaux qui appellent d'abord Conform puis le handler parent s'il existe
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = preProcessValue ? preProcessValue(e.target.value) : e.target.value;
    // debug log — retire en prod si tu veux
    // console.log("[InputConform] handleChange", field.name, newValue);
    control.change(newValue);
    propOnChange?.(e);
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    control.focus();
    propOnFocus?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    control.blur();
    propOnBlur?.(e);
  };

  // Gérer onAccept de MaskInput pour notamment persister copier/coller du champ qui n'est pas fait naturellement
  const handleMaskAccept = (maskedValue: string) => {
    const newValue = preProcessValue ? preProcessValue(maskedValue) : maskedValue;
    // on synchronise la valeur conform
    control.change(newValue);
    propOnMaskAccept?.(newValue);
  };

  const { InputProps, inputProps, ...restForInput } = restProps;

  return (
    <Input
      label={label}
      // props d'accessibilité / name / id etc.
      {...getInputProps(field, { type: "text" })}
      // valeur contrôlée et handlers (ceux-ci prennent priorité)
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      errors={errors}
      data-cy={field.name}
      // le reste des props (placeholder, sx, fullWidth...) sans onChange/onFocus/onBlur
      error={errors.length > 0}
      InputProps={{
        ...(InputProps || {}),
        inputProps: {
          ...(InputProps?.inputProps || {}),
          ...(inputProps || {}),
          // cette fonction arrivera dans TextMaskCustom via TextField/Input
          onMaskAccept: handleMaskAccept,
        },
      }}
      {...restForInput}
    />
  );
}
